import {
  ChangeEvent as ReactChangeEvent,
  CSSProperties,
  FC,
  Fragment,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getEasyLayout,
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import styled from 'styled-components';
import { APP_ENV_CONFIG } from '../../config';
import { ADMIN } from '../../../api/AppRoutes/AdminRoutes/IDs';
import { InterviewStatus, Profile, ProfileWithAdminFields, ProfileWithStatusInfo } from '../../../common/Types';
import { SYM } from '../../Components/MaterialSymbol';
import { TabItem, Tabs } from '../../Components/Tabs';
import { ListItemResults } from '@resistdesign/voltra/api/ORM/ServiceTypes';
import { ProfileDetails } from './ProfileDetails';
import { InterviewStatusLabel } from './InterviewStatusLabel';
import { Avatar } from './Avatar';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

enum AdminSearchMode {
  USER = 'USER',
  ID_CODE = 'ID_CODE',
  INTERVIEW_STATUS = 'INTERVIEW_STATUS',
}

type AdminSearchModeTabItem = TabItem & {
  mode: AdminSearchMode;
};

const SEARCH_MODE_LABELS: Record<AdminSearchMode, string> = {
  [AdminSearchMode.USER]: 'User',
  [AdminSearchMode.ID_CODE]: 'ID Code',
  [AdminSearchMode.INTERVIEW_STATUS]: 'Interview Status',
};

const SEARCH_MODE_RPC_PATHS: Record<AdminSearchMode, string> = {
  [AdminSearchMode.USER]: '/list-users',
  [AdminSearchMode.ID_CODE]: '/list-users-by-id-code',
  [AdminSearchMode.INTERVIEW_STATUS]: '/list-users-by-interview-status',
};

const INTERVIEW_STATUS_LABELS: Record<InterviewStatus, string> = {
  INTERVIEW_NOT_STARTED: 'Interview Not Started',
  INTERVIEW_STARTED: 'Interview Started',
  LAST_QUESTION_STARTED: 'Last Question Started',
  INTERVIEW_COMPLETED: 'Interview Completed',
  INTERVIEW_EXPIRED: 'Interview Expired',
};

const ProfileDropArrowTD = styled.td`
  cursor: pointer;
`;
const {
  layout: Layout,
  areas: { Header, Content },
} = getEasyLayout(styled.div`
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
`)`
  header, auto
  content, 1fr
  \\ 1fr
`;
const ProfileListContent = styled(Content)`
  position: relative;
  overflow: auto;
`;
const CandidateViewIntro = styled.div`
  h3 {
    margin-bottom: 0.25em;
  }

  p {
    margin-bottom: 1em;
  }
`;
const SearchModeBar = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5em;
`;
const SearchModeBarLabel = styled.div`
  flex: 0 0 auto;
`;
const SearchModeBarTabs = styled(Tabs).attrs({ className: 'search-mode-tabs' })`
  &.search-mode-tabs {
    width: unset;

    & > button {
      padding: 0.125em 1em;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;

export type CandidateSearchProps = {
  authorization: string;
  isSystemAdmin: boolean;
  isGroupAdmin: boolean;
  footer?: ReactNode;
};

export const CandidateSearch: FC<CandidateSearchProps> = ({ authorization, isSystemAdmin, isGroupAdmin, footer }) => {
  const [searchMode, setSearchMode] = useState<AdminSearchMode>(AdminSearchMode.USER);
  const [selectedInterviewStatus, setSelectedInterviewStatus] = useState<InterviewStatus | ''>('');
  const [openProfileMap, setOpenProfileMap] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const onSelectedInterviewStatusChange = useCallback(
    ({ target: { value = InterviewStatus.INTERVIEW_COMPLETED } }: ReactChangeEvent<HTMLSelectElement>) => {
      const realValue: InterviewStatus | '' =
        value === '' || Object.values(InterviewStatus).includes(value as InterviewStatus)
          ? (value as InterviewStatus | '')
          : InterviewStatus.INTERVIEW_COMPLETED;

      setSelectedInterviewStatus(realValue);
    },
    []
  );
  const userListRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: SEARCH_MODE_RPC_PATHS[searchMode],
    }),
    [authorization, searchMode]
  );
  const { value: userListInfoValue = {}, onChange: onChangeUserListInfoValue } = useApplicationStateValue(ADMIN.LIST_USERS);
  const searchModeTabItems = useMemo<AdminSearchModeTabItem[]>(
    () =>
      Object.values(AdminSearchMode)
        .filter((mode) => (!isSystemAdmin ? mode !== AdminSearchMode.ID_CODE : true))
        .map(
          (mode) =>
            ({
              mode,
              label: SEARCH_MODE_LABELS[mode],
              onClick: () => {
                setSearchMode(mode);
                setSearchTerm('');
                setSelectedInterviewStatus('');
                void onChangeUserListInfoValue(undefined);
              },
            }) as AdminSearchModeTabItem
        ),
    [isSystemAdmin, onChangeUserListInfoValue]
  );
  const selectedSearchModeIndex = useMemo<number>(
    () => searchModeTabItems.findIndex((item) => item.mode === searchMode),
    [searchMode, searchModeTabItems]
  );
  const { cursor, items: userList = [] } = userListInfoValue as ListItemResults<ProfileWithAdminFields>;
  const visibleUserList = useMemo<ProfileWithAdminFields[]>(() => userList.filter(({ deleted }) => !deleted), [userList]);
  const sortLatestUserList = useMemo(
    () =>
      visibleUserList.sort(
        ({ dateCreated: dateCreatedA = 0 }: Profile, { dateCreated: dateCreatedB = 0 }: Profile) =>
          dateCreatedB - dateCreatedA
      ),
    [visibleUserList]
  );
  const { loading: userListLoading = false, makeRemoteProcedureCall: loadUserList } = useApplicationStateLoader({
    identifier: ADMIN.LIST_USERS,
    remoteProcedureCall: userListRPC,
    manual: true,
  });
  const onSearchInputChange = useCallback(({ target: { value } }: ReactChangeEvent<HTMLInputElement>) => {
    setSearchTerm(value);
  }, []);
  const onToggleProfileOpen = useCallback(({ currentTarget: { dataset } }: ReactMouseEvent<HTMLTableCellElement>) => {
    const { profileId } = dataset as Record<string, string>;

    setOpenProfileMap((prevOpenProfileMap) => ({
      ...prevOpenProfileMap,
      [profileId]: !prevOpenProfileMap[profileId],
    }));
  }, []);
  const modeDependentSearchTerm = useMemo<string>(
    () => (searchMode === AdminSearchMode.INTERVIEW_STATUS ? (selectedInterviewStatus as string) : searchTerm),
    [searchMode, selectedInterviewStatus, searchTerm]
  );
  const onLoadMoreUsers = useCallback(() => {
    void loadUserList(modeDependentSearchTerm, cursor);
  }, [loadUserList, modeDependentSearchTerm, cursor]);
  const showLoadMoreButton = cursor || (searchMode === AdminSearchMode.USER && !searchTerm && userList.length < 1);

  useEffect(() => {
    const tiD = setTimeout(() => {
      if (modeDependentSearchTerm.length > 0) {
        void loadUserList(modeDependentSearchTerm);
      }
    }, 800);

    return () => clearTimeout(tiD);
  }, [modeDependentSearchTerm, loadUserList]);

  return (
    <Layout data-id="ADMIN-CANDIDATE-SEARCH">
      <Header>
        {isGroupAdmin ? (
          <CandidateViewIntro>
            <h3>Candidates</h3>
            <p>Search for candidates who have signed up through your organization.</p>
          </CandidateViewIntro>
        ) : undefined}
        <SearchModeBar>
          <SearchModeBarLabel>Search By:</SearchModeBarLabel>
          <SearchModeBarTabs selectedIndex={selectedSearchModeIndex} tabItems={searchModeTabItems} />
        </SearchModeBar>
        {searchMode === AdminSearchMode.INTERVIEW_STATUS ? (
          <select key={`StatusSelectSearchInput:${searchMode}`} disabled={userListLoading} value={selectedInterviewStatus} onChange={onSelectedInterviewStatusChange} autoFocus>
            <option value="">Select...</option>
            {Object.keys(INTERVIEW_STATUS_LABELS).map((status) => (
              <option key={status} value={status}>{INTERVIEW_STATUS_LABELS[status as InterviewStatus]}</option>
            ))}
          </select>
        ) : (
          <input key={`TextSearchInput:${searchMode}`} type="text" value={searchTerm} onChange={onSearchInputChange} placeholder="Search for users..." autoFocus />
        )}
      </Header>
      <ProfileListContent data-id="CONTENT">
        {userListLoading ? (
          <article aria-busy="true"></article>
        ) : (
          <table>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th>Show Profile</th>
                {isSystemAdmin ? <th>ID Code</th> : undefined}
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                {searchMode === AdminSearchMode.INTERVIEW_STATUS ? <th>Status</th> : undefined}
                {searchMode === AdminSearchMode.INTERVIEW_STATUS ? <th>Status Timestamp</th> : undefined}
              </tr>
            </thead>
            <tbody>
              {sortLatestUserList.map((userItem, index) => {
                const { userId, idCode, groupAdminIdCode, email, username, firstName, lastName, phoneNumber, status, statusTimestamp } = userItem as ProfileWithStatusInfo;

                return (
                  <Fragment key={`User:${index}`}>
                    <tr>
                      <ProfileDropArrowTD data-profile-id={userId} onClick={onToggleProfileOpen}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                          <SYM>{openProfileMap[userId] ? 'arrow_drop_down' : 'arrow_right'}</SYM>
                          <Avatar email={email} name={`${firstName} ${lastName}`} size={32} style={{ marginLeft: '0.5em' }} />
                        </div>
                      </ProfileDropArrowTD>
                      {isSystemAdmin ? (
                        <td><span style={{ backgroundColor: idCode && idCode === searchTerm ? '#88DDFF66' : undefined, padding: '0.5em', borderRadius: '0.5em' }}>{idCode}</span></td>
                      ) : undefined}
                      <td>{username}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'nowrap' }}>
                          {firstName} {lastName}
                          {groupAdminIdCode ? (
                            <span style={{ '--pico-tooltip-background-color': 'darkblue', '--pico-tooltip-color': 'white', color: 'white', backgroundColor: 'darkblue', padding: '0.25em', borderRadius: '50%', marginLeft: '0.5em', borderWidth: 0 } as CSSProperties} data-tooltip="Group Admin">
                              <SYM style={{ fontSize: '1.25em' }}>group</SYM>
                            </span>
                          ) : undefined}
                        </div>
                      </td>
                      <td><a href={`mailto:${email}`}>{email}</a></td>
                      <td><a href={`tel:${phoneNumber}`}>{phoneNumber}</a></td>
                      {searchMode === AdminSearchMode.INTERVIEW_STATUS ? <td><InterviewStatusLabel status={status} /></td> : undefined}
                      {searchMode === AdminSearchMode.INTERVIEW_STATUS ? <td>{typeof statusTimestamp === 'number' ? new Date(statusTimestamp).toLocaleString() : ''}</td> : undefined}
                    </tr>
                    {openProfileMap[userId] ? (
                      <tr>
                        <td></td>
                        <td colSpan={searchMode === AdminSearchMode.INTERVIEW_STATUS ? 5 : 3}>
                          <ProfileDetails authorization={authorization} profileId={userId} adminIsSystemAdmin={isSystemAdmin} />
                        </td>
                      </tr>
                    ) : undefined}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {showLoadMoreButton ? (
          <>
            <br />
            <button onClick={onLoadMoreUsers}>{cursor ? 'More...' : 'List All'}</button>
          </>
        ) : undefined}
        {footer ? <><hr />{footer}</> : undefined}
      </ProfileListContent>
    </Layout>
  );
};
